//	------------------------	------------------------	------------------------
//	Imports
//	------------------------	------------------------	------------------------

import logger from 'xAppLib/libs/logger';
import obj_filter_by_key from "@iscr/xAppLib/helpers/obj_filter_by_key";
import moment from "moment-timezone";

import firebase_auth from "xAppLib/providers/firebase_auth.js";
import API_service from "xAppLib/providers/API_service";

import obj_diff from "xAppLib/helpers/obj_diff";

import org_model from "models/org_model";
import MedicareCard from "xAppLib/UIelems/MedicareCard";
import gtm from "xAppLib/providers/gtm";
import intercom from "xAppLib/providers/intercom";

import validations from "xAppLib/libs/validations";
import { consults_for_followup, consults_for_weight_loss_followup } from "../views/med/utils";
import { rejectOnResErr } from "xAppLib/helpers/rejectOnResErr";
import { ensureIsError, rejectWithResErr } from "@iscr/xAppLib/helpers/rejectWithResErr";

//	------------------------	------------------------	------------------------
//  Globals
//	------------------------	------------------------	------------------------

const DEBUG = false;

//	------------------------	------------------------	------------------------
//  Lists
//	------------------------	------------------------	------------------------

const _ROLE_LIST = {
	10: { c: "root", n: "Root", p: ["root"] },
	9: { c: "admin", n: "Admin", p: ["root", "admin"] },
	8: { c: "supp", n: "Support", p: ["root", "admin"] },
	7: { c: "mark", n: "Analyst", p: ["root", "admin"] },
	6: { c: "doc", n: "Doctor", p: ["root", "admin"] },
	5: { c: "mang", n: "Manager", p: ["root", "admin", "cust_supp", "mang"] },
	4: { c: "nurse", n: app.settings.ileg ? "Operator" : "Nurse", p: ["root", "admin", "cust_supp", "mang_cosm"] },
	3: { c: "pharm", n: "Pharmacist", p: ["root", "admin", "cust_supp", "mang_pharm"] },
	2: { c: "pts", n: "Patient", p: ["root", "admin"] },
	// 1:  { c: 'user', 	n: 'User'		, p: ['root', 'admin']	},
};

const _SEX_LIST_BASE = [
	{ text: "Male", key: "M", value: "M" },
	{ text: "Female", key: "F", value: "F" },
];

const _ABORIGINAL_TORRES_STRAIT = [
	{ text: "No", key: "N", value: "N" },
	{ text: "Yes, Aboriginal", key: "A", value: "A" },
	{ text: "Yes, Torres Strait Islander", key: "TSI", value: "TSI" },
	{ text: "Prefer not to say", key: "", value: "" },
];

const _SEX_LIST = [..._SEX_LIST_BASE];

const _SEX_LIST_FULL = [..._SEX_LIST, { text: "Unknown", key: "N", value: "N" }];

const _DVA_COLOUR_LIST = {
	Gold: "G",
	White: "W",
	Orange: "O",
};

const INDENTATION_STYLE = { paddingLeft: 16 };
const INDENTATION_ERROR_STYLE = { marginLeft: 16 };
const VERTICAL_SPACING_STYLE = { marginTop: 8 };

//	------------------------	------------------------	------------------------
//  Field templates
//	------------------------	------------------------	------------------------

const field = {
	unum: {
		name: "unum",
		label: "User Number",
		read_only: true,
	},
	uid: {
		name: "uid",
		label: "User ID",
		read_only: true,
		type: "uid",
	},
	email: {
		name: "email",
		label: "Email",
		read_only: true,
		disabled: true,
		//icon: 'mail',
	},
	email_required: {
		name: "email",
		label: "Email",
		//icon: 'mail',
	},
	first_name: {
		name: "first_name",
		label: "First name",
		//icon: 'user',
		pattern: /^[A-Za-z][A-Za-z0-9\-' ]+$/,
		validation_message: "Must contain only characters A-Z, a-z, 0-9, -, ' and spaces",
	},
	first_name_min: {
		name: "first_name",
		label: "First name",
		//icon: 'user',
	},
	last_name: {
		name: "last_name",
		label: "Last name",
		//icon: 'user',
		pattern: /^[A-Za-z][A-Za-z0-9\-' ]+$/,
		validation_message: "Must contain only characters A-Z, a-z, 0-9, -, ' and spaces",
	},
	last_name_min: {
		name: "last_name",
		label: "Last name",
		//icon: 'user',
	},
	dob: {
		name: "dob",
		label: "Date of Birth",
		type: "dob_date",
		//icon: 'calendar',
	},
	sex: {
		name: "sex",
		label: "Sex assigned at birth",
		type: "select",
		options: _SEX_LIST_BASE,
		note: "We realise this is a sensitive question for some patients. However, in order for our doctors to ensure your safety and prescribe the correct medications for you it is important that we obtain this information.",
	},
	sex_adv: {
		name: "sex",
		label: "Sex",
		type: "select",
		placeholder: "⚤ Sex",
		options: _SEX_LIST_FULL,
	},
	mobile: {
		name: "mobile",
		label: "Mobile phone number",
		type: "masked",
		mask: [/\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/],
		placeholder: "04XX XXX XXX",
		pattern: /^04\s?[0-9]{2}\s?[0-9]{3}\s?[0-9]{3}$/,
		//icon: 'mobile',
		//valid_not_required: true,
	},
	medicare_single: {
		name: "medicare",
		label: "Medicare number (including Individual Reference Number)",
		type: "masked",
		mask: [/\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, /\d/, /\d/, " ", /\d/, " ", /\d/],
		placeholder: "____ _____ _ _",
		//icon: 'id card outline',
		valid_not_required: true,
		validate_function: validations.validate_medicare,
	},
	medicare: {
		name: "medicare",
		label: "Medicare Number",
		type: MedicareCard,
		valid_not_required: true,
		validate_function: validations.validate_medicare,
		inline_edit: false,
	},
	has_dva_card: {
		name: "has_dva_card",
		type: "radio-button",
		options: ["No", "Yes"],
		checkbox_style: INDENTATION_STYLE,
		valid_not_required: true,
		client_only: true,
	},
	dva: {
		name: "dva",
		label: `Department of Veteran Affairs (DVA) (optional)`,
		type: "dva",
		valid_not_required: true,
		validate_function: value => validations.validate_dva(value), // Need both fields entered
		validate_dva_number: value => validations.validate_dva_number(value),
		validate_dva_colour: value => validations.validate_dva_colour(value),
		dva_colour_options: Object.keys(_DVA_COLOUR_LIST).map((s, i) => {
			return { key: `s_i_${i}`, value: _DVA_COLOUR_LIST[s], text: s };
		}),
		// dva_number_icon: 'id card outline',
		dva_number_placeholder: "AAXXNNNN[A]",
		format: v => (v && `${v.number} ${v.colour}`) || "",
	},
	has_conc_card: {
		name: "has_conc_card",
		type: "radio-button",
		options: ["No", "Yes"],
		checkbox_style: INDENTATION_STYLE,
		valid_not_required: true,
		client_only: true,
	},
	conc_card: {
		header: "Concessions",
		name: "conc_card",
		label: "Concession card (optional)",
		//icon: 'id card',
		valid_not_required: true,
		placeholder: "000-000-000A",
		type: "masked",
		mask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /[A-Za-z]/],
		pattern: /^\d{9}[A-Za-z]$/,
		val_proc: value => value.replace(/-/g, ""),
	},

	full_address: {
		name: "full_address",
		label: "Primary Residential Address",
		type: "address",
		// icon: 'address card',
		mode: "components",
		valid_not_required: false,
		validate_function: value =>
			/^\d{4}$/.test(value.postcode) && value.suburb.length && value.address.length && value.state.length,
		validation_message: value => {
			if (!/^\d{4}$/.test(value.postcode)) return "Must be a valid postcode";
			if (!value.suburb.length) {
				return "Must be a valid suburb";
			}
			if (!value.address.length) {
				return "Must be a valid address";
			}
			if (!value.state.length) {
				return "Must select a state";
			}
			return false;
		},
	},
	full_address_autocomplete: {
		name: "full_address_autocomplete",
		label: "Primary Residential Address",
		modalHeader: "Primary Residential Address",
		confirm_text: "Confirm Address",
		type: "address_new",
		mode: "components",
		valid_not_required: true,
	},
	update_shipping: {
		name: "update_shipping",
		type: "checkbox",
		options: [{ c: "shipping", n: "Also update my treatment plan deliveries to this address" }],
		checkbox_style: INDENTATION_STYLE,
		valid_not_required: true,
	},
	suburb: {
		// Depreciated
		name: "suburb",
		label: "Suburb",
		read_only: true,
	},
	state: {
		// Depreciated
		name: "state",
		label: "State",
		read_only: true,
	},
	postcode: {
		// Depreciated
		name: "postcode",
		label: "Postcode",
		read_only: true,
	},
	geo: {
		name: "geo",
		label: "Geo",
		read_only: true,
		type: "json",
		view_path: "formatted_address",
	},
	xtra: {
		name: "xtra",
		label: "Extra Fields",
		type: "json",
		// default: {
		// 	AHPRA: ""
		// },
		valid_not_required: true,
	},
	extras: {
		name: "extras",
		label: "Extra Conf",
		type: "json",
		valid_not_required: true,
	},
	active: {
		name: "active",
		label: "Active",
		type: "bool",
		valid_not_required: true,
	},
	cre_tm: {
		name: "cre_tm",
		label: "Created",
		read_only: true,
	},
	acc_tm: {
		name: "acc_tm",
		label: "Last Access",
		read_only: true,
	},
	upd_tm: {
		name: "upd_tm",
		label: "Updated",
		read_only: true,
	},
	auth: {
		name: "auth",
		label: "Authentication Meta",
		read_only: true,
		type: "json",
		view_path: "metadata",
	},
	booking_config: {
		name: "booking_config",
		label: "Booking Config",
		valid_not_required: true,
		type: "json",
		view_path: "booking_config",
	},
	password: {
		name: "password",
		label: "Password",
		type: "password",
		with_validator: true,
		// icon: 'lock',
	},
	verify_password: {
		name: "verify_password",
		label: "Confirm new password",
		type: "password",
		validation_message: value => {
			if (value) {
				return "Passwords must match";
			}
			return "Field is missing";
		},
		// icon: 'lock',
	},
	nu_org: {
		name: "nu_org",
		label: "Organisation",
		type: "org",
		param_name: "oid",
		valid_not_required: true,
	},
	claims: {
		name: "claims",
		label: "Role",
		type: "role",
		valid_not_required: true,
	},
	send_pass: {
		name: "send_pass",
		label: "Send Password in Email",
		type: "checkbox",
		options: [{ c: "yes", n: "Include password in welcome email" }],
		valid_not_required: true,
	},
	no_email: {
		name: "no_email",
		label: "Do Not Email User",
		type: "checkbox",
		options: [{ c: "no", n: "Don't send welcome email" }],
		valid_not_required: true,
	},
	note: {
		name: "note",
		label: "Additional note to email",
		type: "textarea",
		value: "To use your account and access your scripts, login at https://pharmapp.instantscripts.com.au",
		valid_not_required: true,
	},
	mfa: {
		name: "mfa",
		label: "Multi-factor Authentication",
		type: "bool",
		valid_not_required: true,
	},
	consent: {
		name: "consent",
		label: "Privacy",
		type: "privacy",
		// auto:true,
	},
	atsi: {
		name: "atsi",
		label: "Aboriginal or Torres Strait Islander origin?",
		type: "select",
		options: _ABORIGINAL_TORRES_STRAIT,
		valid_not_required: true,
	},
	has_em_con: {
		name: "has_em_con",
		label: "Emergency Contact",
		type: "radio-button",
		options: [
			{ value: "yes", text: "Add contact", key: "y" },
			{ value: "no", text: "None", key: "n" },
		],
		valid_not_required: true,
		client_only: true,
	},
	em_con_name: {
		name: "em_con_name",
		label: "Emergency Contact Name",
		//icon: 'user',
		pattern: /^[A-Za-z][A-Za-z0-9\-' ]+$/,
		validation_message: "Must contain only characters A-Z, a-z, 0-9, -, ' and spaces",
		valid_not_required: true,
	},
	em_con_mobile: {
		name: "em_con_mobile",
		label: "Emergency Contact Mobile",
		type: "masked",
		mask: [/\d/, /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/, " ", /\d/, /\d/, /\d/],
		placeholder: "04XX XXX XXX",
		pattern: /^04\s?[0-9]{2}\s?[0-9]{3}\s?[0-9]{3}$/,
		valid_not_required: true,
	},
	em_con_rel: {
		name: "em_con_rel",
		label: "Relationship of emergency contact to patient",
		pattern: /^[A-Za-z][A-Za-z0-9\-' ]+$/,
		validation_message: "Must contain only characters A-Z, a-z, 0-9, -, ' and spaces",
		valid_not_required: true,
	},
	cons_has_allergies: {
		name: "cons_has_allergies",
		type: "checkbox",
		options: [{ c: "no", n: "No allergies" }],
		checkbox_style: INDENTATION_STYLE,
		valid_not_required: true,
	},
	cons_allergy: {
		header: "Do you have any allergies?",
		label: "Specify your allergies",
		placeholder: "Please list any allergies you have",
		name: "cons_allergy",
		type: "text",
		container_style: INDENTATION_STYLE,
		valid_not_required: v => v.cons_has_allergies === "no",
	},
	registration_type: {
		label: "Medical practitioners registration type",
		placeholder: "Registration type",
		name: "registration_type",
		type: "text",
		valid_not_required: true,
	},
	qualifications: {
		label: "Specify your qualification, training and experience",
		placeholder: "Qualifications",
		name: "qualifications",
		type: "text",
		valid_not_required: true,
	},
	chro_cond: {
		name: "chro_cond",
	},
	cons_has_meds: {
		name: "cons_has_meds",
		type: "checkbox",
		options: [{ c: "no", n: `I'm not taking any medications` }],
		checkbox_style: INDENTATION_STYLE,
		valid_not_required: true,
	},
	cons_meds: {
		header: "What medications are you currently taking?",
		label: "Specify your medications",
		placeholder: "Please list any medication you are currently taking",
		name: "cons_meds",
		type: "text",
		container_style: INDENTATION_STYLE,
		valid_not_required: v => v.cons_has_meds === "no",
	},
	cons_has_medhist: {
		name: "cons_has_medhist",
		type: "checkbox",
		options: [{ c: "no", n: `I don't have a medical history` }],
		checkbox_style: INDENTATION_STYLE,
		valid_not_required: true,
	},
	cons_medhist: {
		header: "Brief medical history",
		label: "Provide a summary of your medical history",
		placeholder: "History",
		name: "cons_medhist",
		type: "textarea",
		rows: 2,
		container_style: INDENTATION_STYLE,
		valid_not_required: v => v.cons_has_medhist === "no",
	},
	height: {
		name: "height",
		type: "number",
		label: "Height (cm) (optional)",
		valid_not_required: true,
		validate_function: value => value > 30,
		validation_message: () => "Please enter height in cm",
	},
	weight: {
		name: "weight",
		type: "number",
		label: "Weight (kg) (optional)",
		valid_not_required: true,
		validate_function: value => value > 0,
		validation_message: () => "Please enter weight in kg",
	},
	yogp: {
		name: "yogp",
		type: "object",
		valid_not_required: v => !v.yogp_want,
		update_profile: true,
		nullable: true,
	},
	yogp_want: {
		name: "yogp_want",
		type: "checkbox",
		valid_not_required: true,
		client_only: true,
	},
	// note that doctor AHPRA numbers are stored in the Firebase docs collection and not in the user table
	ahpra: {
		name: "ahpra",
		label: "AHPRA Number",
		placeholder: "AHPRA Number",
		valid_not_required: true,
	},
};

export const field_dva = field.dva;
export const field_mobile = field.mobile;
export const field_full_address = field.full_address;

const header = {
	user_details: { type: 'header', label: "User details" },
	security: { type: 'header', label: "Security" },
	welcome_email: { type: 'header', label: "Welcome email" },
	emergency_contact: { type: 'header', label: "Emergency contact" },
	medical_history: { type: 'header', label: "Medical history" },
	qualifications: { type: 'header', label: "Qualifications" },
}

//	------------------------	------------------------	------------------------
//  Field collections
//	------------------------	------------------------	------------------------

const _PROF_FIELDS_ADMIN = [
	field.unum,
	field.email,
	header.user_details,
	field.first_name_min,
	field.last_name_min,
	{ ...field.dob, valid_not_required: true },
	{ ...field.sex_adv, valid_not_required: true },
	{ ...field.mobile, valid_not_required: true },
	{ ...field.medicare, admin_add_hidden: true },
	field.dva,
	field.conc_card,
	{ ...field.full_address_autocomplete, name: "full_address", valid_not_required: true, disable_profile: true },
	field.suburb,
	field.state,
	field.postcode,
	field.geo,
	field.xtra,
	app.user?.claims?.root && field.extras,
	field.active,
	field.cre_tm,
	field.acc_tm,
	field.upd_tm,
	field.auth,
	header.emergency_contact,
	field.em_con_name,
	field.em_con_mobile,
	field.em_con_rel,
	header.qualifications,
    field.ahpra,
	field.registration_type,
	field.qualifications,
	header.medical_history,
	{ ...field.cons_allergy, valid_not_required: true, showif: { type: "showIfNot", value: "cons_has_allergies" } },
	{ ...field.cons_has_allergies, valid_not_required: true },
	{ ...field.cons_medhist, valid_not_required: true, showif: { type: "showIfNot", value: "cons_has_medhist" } },
	{ ...field.cons_has_medhist, client_only: true, valid_not_required: true },
	{ ...field.cons_meds, valid_not_required: true, showif: { type: "showIfNot", value: "cons_has_meds" } },
	{ ...field.cons_has_meds, client_only: true, valid_not_required: true },
	field.booking_config,
].filter(Boolean);

const _PROF_FIELDS_MANG = [
	field.email,
	header.user_details,
	field.first_name_min,
	field.last_name_min,
	{ ...field.dob, valid_not_required: true },
	field.sex,
	field.mobile,
	field.medicare,
	field.dva,
	field.conc_card,
	{ ...field.full_address_autocomplete, name: "full_address", valid_not_required: true, disable_profile: true },
	field.suburb,
	field.state,
	field.postcode,
	field.geo,
	field.xtra,
	field.active,
	header.emergency_contact,
	field.em_con_name,
	field.em_con_mobile,
	field.em_con_rel,
	header.qualifications,
	field.registration_type,
	field.qualifications,
	header.medical_history,
	{ ...field.cons_allergy, valid_not_required: true, showif: { type: "showIfNot", value: "cons_has_allergies" } },
	{ ...field.cons_has_allergies, valid_not_required: true },
	{ ...field.cons_medhist, valid_not_required: true, showif: { type: "showIfNot", value: "cons_has_medhist" } },
	{ ...field.cons_has_medhist, client_only: true, valid_not_required: true },
	{ ...field.cons_meds, valid_not_required: true, showif: { type: "showIfNot", value: "cons_has_meds" } },
	{ ...field.cons_has_meds, client_only: true, valid_not_required: true },
];

const _PROF_FIELDS_ADM_SGUP = [
	header.user_details,
	field.email_required,
	field.first_name_min,
	field.last_name_min,
	{ ...field.dob, valid_not_required: true },
	{ ...field.sex_adv, valid_not_required: true },
	{ ...field.mobile, valid_not_required: true },
	field.medicare,
	field.dva,
	field.conc_card,
	{ ...field.full_address_autocomplete, valid_not_required: false, disable_profile: true },
	header.security,
	field.password,
	field.nu_org,
	field.claims,
	header.welcome_email,
	field.no_email,
	field.send_pass,
	field.note,
	header.emergency_contact,
	field.em_con_name,
	field.em_con_mobile,
	field.em_con_rel,
];

const _PROF_FIELDS_DOC_SGUP = [
	header.user_details,
	{ ...field.email, read_only: false, disabled: false },
	field.first_name_min,
	field.last_name_min,
	field.dob,
	field.sex,
	field.mobile,
	{ ...field.medicare, admin_add_hidden: true },
	field.dva,
	{ ...field.conc_card, admin_add_hidden: true },
	{ ...field.full_address_autocomplete, valid_not_required: false, disable_profile: true },
	header.security,
	field.password,
	header.welcome_email,
	field.no_email,
	field.send_pass,
	field.note,
	field.consent,
	header.emergency_contact,
	field.em_con_name,
	field.em_con_mobile,
	field.em_con_rel,
];

const _PROF_FIELDS_PTS_BASE = [
	field.first_name,
	field.last_name,
	field.medicare,
	field.dva,
	field.conc_card,
	{ ...field.full_address, valid_not_required: false },
	field.atsi,
	field.em_con_name,
	field.em_con_mobile,
	field.em_con_rel,
];

/// --------

const _PROF_FIELDS_SGUP = [
	{ ...field.email, read_only: false, disabled: false },
	..._PROF_FIELDS_PTS_BASE,
	{ ...field.dob, valid_not_required: true },
	{ ...field.sex, valid_not_required: true },
	{ ...field.mobile, valid_not_required: true },

	field.password,
	field.verify_password,
	field.consent,
];

const _PROF_FIELDS_PTS_SGUP = [
	// Conformance MA-4
	field.email,
	field.first_name,
	field.last_name,
	field.dob,
	field.sex,
	field.mobile,
	field.medicare,
	field.dva,
	field.conc_card,
	{ ...field.full_address_autocomplete, valid_not_required: false },
	field.password,
	field.verify_password,
	field.consent,
	field.em_con_name,
	field.em_con_mobile,
	field.em_con_rel,
];

const _PROF_FIELDS_MY = [
	{
		name: "desc",
		label: "Profile Name / Description",
		type: "hidden",
		value: null,
	},
	field.chro_cond,
	field.cons_has_allergies,
	field.cons_allergy,
	field.email,
	field.cons_meds,
	field.cons_medhist,
	..._PROF_FIELDS_PTS_BASE,
	{ ...field.dob, valid_not_required: true },
	{ ...field.sex, valid_not_required: true },
	{ ...field.mobile, valid_not_required: true },
	{ ...field.cons_has_meds, client_only: true },
	{ ...field.cons_has_medhist, client_only: true },
	{ ...field.yogp },
	{ ...field.yogp_want },
];
const _PROF_FIELDS_MY_CHILD = [
	{
		name: "desc",
		label: "Profile Name / Description",
	},
	field.chro_cond,
	field.cons_has_allergies,
	field.cons_allergy,
	field.email,
	field.cons_meds,
	field.cons_medhist,
	..._PROF_FIELDS_PTS_BASE,
	{ ...field.dob },
	{ ...field.sex, valid_not_required: true },
	{ ...field.mobile },
	{ ...field.cons_has_meds, client_only: true },
	{ ...field.cons_has_medhist, client_only: true },
	{ ...field.yogp },
	{ ...field.yogp_want },
];

const _PROF_MEDICAL_HISTORY = [
	field.cons_has_allergies,
	{ ...field.cons_allergy, valid_not_required: true },
	{ ...field.cons_has_meds, client_only: true },
	{ ...field.cons_meds, valid_not_required: true, container_style: VERTICAL_SPACING_STYLE },
	{ ...field.cons_has_medhist, client_only: true },
	{
		...field.cons_medhist,
		valid_not_required: true,
		container_style: { ...VERTICAL_SPACING_STYLE, ...INDENTATION_STYLE },
	},
];

const _PROF_FIELDS_IS2 = [
	field.first_name,
	field.last_name,
	field.height,
	field.weight,
	{
		...field.dob,
		valid_not_required: true,
		validate_function: val =>
			/\d{4}-\d{2}-\d{2}/.test(val) &&
			moment(val).isValid() &&
			moment().diff(val, "years") >= 0 &&
			moment().diff(val, "years") <= 120,
	},
	{ ...field.full_address, valid_not_required: false, label: "Home address", type: "address_new", modalHeader: "Home address", confirm_text: "Confirm home address" },
	field.update_shipping,
	field.mobile,
	{ ...field.sex, valid_not_required: true, type: "radio-button" },
	{ ...field.atsi, type: "radio-button" },
	field.has_em_con,
	{ ...field.em_con_name, showif: { type: "showIfNot", value: "has_em_con" } },
	{ ...field.em_con_mobile, showif: { type: "showIfNot", value: "has_em_con" } },
	{ ...field.em_con_rel, label: "Relationship to patient", showif: { type: "showIfNot", value: "has_em_con" } },
	{ ...field.medicare, header: "Medicare details" },
	{ ...field.has_conc_card, header: "Concessions", label: "Do you have a concession card?" },
	{ ...field.conc_card, header: "", valid_not_required: true, showif: { type: "showIfNot", value: "has_conc_card" } },
	{
		...field.has_dva_card,
		label: "Do you have a Department of Veteran Affairs (DVA) card?",
		valid_not_required: true,
	},
	{ ...field.dva, label: "", showif: { type: "showIfNot", value: "has_dva_card" }, valid_not_required: true },
	{
		...field.chro_cond,
		label: "Chronic conditions:",
		placeholder: "",
		header: "Medical information",
		rows: 1,
		valid_not_required: true,
	},
	{
		...field.cons_medhist,
		header: null,
		label: "Anything the doctor should know about:",
		placeholder: "",
		container_style: null,
		rows: 1,
		valid_not_required: true,
	},
	{
		...field.cons_allergy,
		header: null,
		label: "Allergies:",
		placeholder: "",
		container_style: null,
		rows: 1,
		valid_not_required: true,
	},
	{ ...field.yogp, header: "Your doctor details", valid_not_required: true },
];

export default class user_model extends firebase_auth {
	// ---- --------------------------------------------  --------------------------------------------
	// ---- --------------------------------------------  --------------------------------------------

	static get ROLE_LIST() {
		return _ROLE_LIST;
	}
	static get PROF_FIELDS_ADMIN() {
		return _PROF_FIELDS_ADMIN;
	}
	static get PROF_FIELDS_MANG() {
		return _PROF_FIELDS_MANG;
	}
	static get PROF_FIELDS_ADM_SGUP() {
		return _PROF_FIELDS_ADM_SGUP;
	}
	static get PROF_FIELDS_DOC_SGUP() {
		return _PROF_FIELDS_DOC_SGUP;
	}
	static get PROF_FIELDS_SGUP() {
		return _PROF_FIELDS_SGUP;
	}
	static get PROF_FIELDS_PTS_SGUP() {
		return _PROF_FIELDS_PTS_SGUP;
	}
	static get PROF_FIELDS_MY() {
		return _PROF_FIELDS_MY;
	}
	static get PROF_FIELDS_MY_CHILD() {
		return _PROF_FIELDS_MY_CHILD;
	}
	static get PROF_MEDICAL_HISTORY() {
		return _PROF_MEDICAL_HISTORY;
	}
	static get PROF_FIELDS_IS2() {
		return _PROF_FIELDS_IS2;
	}

	// ----	--------------------------------------------	--------------------------------------------
	// ----	--------------------------------------------	--------------------------------------------

	static get_atsi_label(k) {
		return _ABORIGINAL_TORRES_STRAIT.find(m => m.key === k)?.text || "Not specified";
	}

	static check_access(a, p) {
		let r = false;
		let cat_nm;

		switch (a) {
			case "set_own_device_org":
				r = app.user?.claims?.admin || app.user?.claims?.supp || app.user?.claims?.mang || app.user?.claims?.nurse;
				break;
			case "pg_med":
				r =
					app.user?.claims?.admin ||
					(p == "iLeg_cat" &&
						app.user?.org?.type == "leg" &&
						p == org_model.org_conf_itm(app.user.org, "meds") &&
						app.user?.claims?.nurse &&
						app.user.claims.oid) ||
					(p != "pharm" &&
						app.dvc?.org?.type == "cosm" &&
						p == org_model.org_conf_itm(app.user.org, "meds") &&
						app.user?.claims?.nurse &&
						app.user.claims.oid) ||
					(p != "pharm" && p == org_model.org_conf_itm(app.user.org, "meds") && app.acl.is_doc_presc) ||
					(p == "pharm" && app.dvc?.org?.type == "pharm") ||
					// || app.user?.claims?.pts
					((p == "pharm" || p == "patho") &&
						app.user.user_in &&
						org_model.org_conf_itm(app.runtime.org, "is_mc"));
				// && document.location.hostname!="pharmapp.instantscripts.com.au"
				// && document.location.hostname!="cosmapp.instantscripts.com.au"
				// r = app.dvc.data && app.dvc.data.pharm_id
				// 		|| app.dvc.data && app.dvc.data.org_id && app.user && app.user.claims && app.user.claims.nurse
				// 		|| app.user && app.user.claims && app.user.claims.admin
				DEBUG &&
					console.log(
						"check_access ",
						a,
						p,
						app.user?.org?.type,
						org_model.org_conf_itm(app.user.org, "meds"),
						r,
					);
				break;

			// nurses with or without a registered device can access cosmetic complication consults
			case "pg_cosm_comp":
				r = (app.dvc?.org?.type == "cosm" || !app.dvc?.org?.type) &&
						p == org_model.org_conf_itm(app.user.org, "meds") &&
						app.user?.claims?.nurse &&
						app.user.claims.oid;
				break;

			case "pg_cat":
				cat_nm = p?.replace(/^_/, "") || "pharm";
				// console.log('check_access ', a, r, p, org_model.org_conf_itm(app.user.org, 'meds'));
				r =
					app.user?.claims?.admin ||
					(!p && app.dvc?.org?.type == "pharm") ||
					cat_nm == "pharm" ||
					cat_nm == "patho" ||
					(cat_nm == org_model.org_conf_itm(app.user.org, "meds") && app.dvc?.org?.type == "cosm") ||
					(app.user?.org?.type == "leg" && (app.user?.claims?.nurse || app.user?.claims?.mang)) ||
					(cat_nm == org_model.org_conf_itm(app.user.org, "meds") && app.acl.is_doc_presc);

				// || app.user?.claims?.mang
				// || app.user?.claims?.nurse
				// || !p && app.user?.claims?.doc
				// && app.dvc.org?.type!='cosm'
				// && app.user && app.user.prof /*&& !app.user.prof.claims*/
				// && org_model.org_conf_itm(app.user.org, 'is_mc')
				// && document.location.hostname!="pharmapp.instantscripts.com.au"
				// && document.location.hostname!="cosmapp.instantscripts.com.au"
				// r = !p && app.dvc.data && app.dvc.data.pharm_id
				// 		|| p && p=='_cosm' && app.dvc.data && app.dvc.data.org_id && app.user && app.user.claims && (app.user.claims.nurse||app.user.claims.mang||app.user.claims.doc)
				// 		|| app.user && app.user.claims && app.user.claims.admin
				break;

			case true:
			case "user":
				r = app.user.user_in;
				if (p && app.user.user_det && app.user.user_det.providerData[0].providerId != p) {
					r = false;
				}
				break;

			case "cust_supp":
				r = app.user?.claims?.admin || app.user?.claims?.supp;
				break;

			case "pulse_rewards":
				r = app.user?.claims?.admin || app.user?.claims?.supp;
				break;

			case "pulse_rewards_stats":
				r = app.user?.claims?.admin || app.user?.claims?.supp || app.user?.claims?.mark;
				break;

			case "pulse_rewards_points_edit":
				r = app.user?.claims?.admin;
				break;

			case "employee_benefits":
				r = app.user?.claims?.admin || app.user?.claims?.supp;
				break;

			case "employee_benefits_stats":
				r = app.user?.claims?.admin || app.user?.claims?.supp || app.user?.claims?.mark;
				break;

			case "edit_user_claims":
				r = app.user?.claims?.admin || app.user?.claims?.mang;
				break;

			case "view_orgs":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.user?.claims?.mark ||
					app.user?.claims?.doc ||
					app.user?.claims?.mang;
				break;

			case "view_all_orgs":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.user?.claims?.mark ||
					app.user?.claims?.doc;
				break;

			case "view_my_orgs":
				r = app.user?.claims?.mang || app.user?.claims?.nurse;
				break;

			case "delete_org":
				r = Boolean(app.user?.claims?.root);
				break;

			case "phts_add":
			case "phts_view":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.user?.claims?.mark ||
					app.user?.claims?.mang ||
					app.user?.claims?.doc ||
					app.user?.claims?.nurse;
				// || app.user?.claims?.pharm;
				break;

			case "attch_view":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					(app.user?.claims?.doc && org_model.org_conf_itm(app.user.org, "is_GP"));
				break;

			case "pts_srch":
			case "pts_view":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.user?.claims?.mark ||
					app.user?.claims?.mang ||
					app.user?.claims?.doc ||
					app.user?.claims?.nurse ||
					app.user?.claims?.pharm;
				break;

			case "root":
				r = app.user && app.user.claims && app.user.claims.root;
				break;

			case "admin":
			case "admin_med":
				r = app.user && app.user.claims && app.user.claims.admin;
				break;

			case "user_admin":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.acl.is_doc_admin ||
					(app.user?.claims?.mang && ((app.settings.ileg && app.acl.is_leg_oper) || !app.settings.ileg));
				break;

			case "manage_tenant_users":
				r = app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					(
						app.acl.is_tenant_manager &&
						(!p || (org_model.org_conf_itm(app.user.org, "actions")??{})[p] === true)
					);
				break;

			case "admin_devices":
				r = app.user?.claims?.admin || (app.user?.claims?.supp && app.user?.claims?.mang);
				break;

			case "GP":
				r = app.user?.claims?.admin || (app.user?.claims?.doc && org_model.org_conf_itm(app.user.org, "is_GP"));
				break;

			case "can_prescribe":
				r =
					app.user?.claims?.admin ||
					app.acl.is_NP ||
					(app.user?.claims?.doc && org_model.org_conf_itm(app.user.org, "is_GP"));
				break;

			case "doccons_view":
				r =
					app.user?.claims?.admin ||
					(app.user?.claims?.doc && org_model.org_conf_itm(app.user.org, "is_GP")) ||
					app.user;
				break;

			case "scripts_stats":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.mark ||
					app.user?.claims?.mang ||
					app.user?.claims?.doc ||
					app.user?.claims?.nurse ||
					app.user?.claims?.pharm;
				break;

			case "docs_stats":
				r = app.user?.claims?.admin || app.user?.claims?.doc || app.user?.prof?.xtra?.show_docs_stats;
				break;

			case "manage_landing_pages":
			case "manage_feedback":
				r = app.user?.claims?.admin || app.user?.claims?.supp || app.user?.claims?.mark;
				break;

			case "use_stats":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.mark ||
					app.user?.prof?.xtra?.show_docs_scr_stats ||
					(app.user?.org?.type === "patho_partner" && app.user?.claims?.mang && p?.type === "pathoreqs");
				break;

			case "priceline_stats":
				r = app.user?.prof?.xtra?.priceline_stats;
				break;

			case "show_support":
				r =
					!app.settings.icosm &&
					(app.dvc.org ||
						(app.user &&
							app.user.claims &&
							(app.user.claims.admin ||
								app.user.claims.mark ||
								app.user.claims.mang ||
								app.user.claims.nurse ||
								app.user.claims.pharm)));
				break;

			case "show_contact":
				r = !app.user || !(app.user.claims?.admin || app.user.claims?.mark);
				break;

			case "scripts_list":
				r =
					app.user &&
					app.user.claims &&
					!app.acl.is_spon &&
					(app.user.claims.admin ||
						app.user.claims.supp ||
						app.user.claims.mang ||
						app.user.claims.doc ||
						app.user.claims.nurse ||
						app.user.claims.pharm);
				break;

			case "script_details":
				r =
					app.user &&
					app.user.claims &&
					!app.acl.is_spon &&
					(app.user.claims.admin ||
						app.user.claims.supp ||
						app.user.claims.mang ||
						(app.user.claims.doc && [p?.doc_id, p].includes(app.user.claims.doc_id)) ||
						app.user.claims.nurse ||
						app.user.claims.pharm);
				break;

			case "view_scripts":
				r =
					app.user &&
					app.user.claims &&
					(app.user.claims.admin ||
						app.user.claims.supp ||
						app.user.claims.mang ||
						app.user.claims.doc ||
						app.user.claims.nurse ||
						app.user.claims.pharm);
				break;

			case "prescs":
				r = app.user?.claims?.admin || app.user?.claims?.doc || app.user?.claims?.supp;
				break;

			case "adm_treatment_plan":
				r = app.acl.is_admin || app.acl.is_supp || app.acl.is_doc_gp;
				break;

			case "buy_prods":
				r =
					app.user?.claims?.admin ||
					(app.user?.claims?.mang && app.user.org?.conf?.prods_sale?.includes("med_cosm")) ||
					app.user?.prof?.xtra?.prods_sale?.includes("med_cosm") ||
					(["bpm", "pay_us"].includes(p) && app.user.user_in);
				break;

			case "review_mhcp":
				r = app.acl.is_admin || app.acl.is_supp;
				break;

			case "view_trans":
				r =
					app.user?.claims?.root ||
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.user.org.type == "supp" ||
					(app.user?.claims?.mark && app.user?.prof?.xtra?.show_prod_trans) ||
					app.user.claims.nurse ||
					app.user.claims.mang ||
					app.user.claims.doc ||
					app.user.org?.conf?.prods_sale?.includes("med_cosm");
				break;

			case "view_docpay":
				r =
					app.user?.claims?.root ||
					app.user?.claims?.admin ||
					(app.user?.claims?.mark && app.user?.prof?.xtra?.show_docpay_consumer) ||
					(app.user?.claims?.mark && app.user?.prof?.xtra?.show_docpay_cosmetics);
				break;

			// The API's own check is the same - see docpay:master_data:upload in the backend's user_model
			case "docpay:master_data:upload":
				r =
					app.user?.claims?.root ||
					app.user?.claims?.admin ||
					(app.user?.claims?.mark && app.user?.prof?.xtra?.upload_docpay_master_data);
				break;
			

			case "view_all_prod_trans":
				r =
					app.user?.claims?.root ||
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					(app.user?.claims?.mark && app.user?.prof?.xtra?.show_prod_trans);
				break;

			case "raw_data":
				r = app.user && app.user.claims && app.user.claims.root;
				break;

			case "doc_raw_data":
				r =
					app.user &&
					app.user.claims &&
					(app.user.claims.root || app.user.claims.admin || app.user.claims.supp);
				break;

			case "nurse":
				r = app.user.user_in && app.user.claims && app.user.claims.nurse && app.user.claims.oid;
				break;

			case "mang":
				r = app.user.user_in && app.user.claims && app.user.claims.mang && app.user.claims.oid;
				break;

			case "mang_cosm":
				r =
					app.user.user_in &&
					app.user.claims &&
					app.user.claims.mang &&
					app.user.claims.oid &&
					app.user.org?.type == "cosm";
				break;

			case "audit_user":
				r =
					app.user.user_in &&
					app.user.claims.oid &&
					["cosm"].includes(app.user.org?.type) &&
					(app.user.claims?.nurse || app.user.claims?.mang);
				break;
			case "audit_reviewer":
				r = app.user.user_in && (app.user.claims?.admin || app.user.claims?.supp);
				break;
			case "audit_cosm":
				r =
					app.user.user_in &&
					((app.user.claims.oid &&
						["cosm"].includes(app.user.org?.type) &&
						(app.user.claims?.nurse || app.user.claims?.mang)) ||
						app.user.claims?.admin ||
						app.user.claims?.supp);
				break;
			case "treatment_plan_manage_cosm":
				r =
					app.user.user_in &&
					((app.user.claims.oid &&
						["cosm"].includes(app.user.org?.type) &&
						(app.user.claims?.nurse || app.user.claims?.mang)) ||
						app.user.claims?.admin ||
						app.user.claims?.supp);
				break;

			case "mang_pharm":
				r =
					app.user.user_in &&
					app.user.claims &&
					app.user.claims.mang &&
					app.user.claims.oid &&
					app.user.org?.type == "pharm";
				break;

			case "pharm":
				r = app.user.user_in && app.user.claims && app.user.claims.pharm && app.user.claims.oid;
				break;

			case "payment_profile":
				r =
					(app.user?.claims?.doc && org_model.org_conf_itm(app.user.org, "is_GP")) ||
					(app.user.claims?.doc && app.user.claims.doc_id);
				break;

			case "doc":
				r = app.user.claims.admin || app.user.claims.supp || (app.user.claims?.doc && app.user.claims.doc_id);
				break;

			case "doc_gp":
				r =
					app.user.claims.admin ||
					app.user.claims.supp ||
					(app.user.claims?.doc && app.user.claims.doc_id && !app.acl.is_NP);
				break;

			case "show_wr_stats":
				r =
					app.user.claims.admin ||
					app.user.claims.supp ||
					(app.user.claims?.doc && app.user.claims.doc_id && !app.acl.is_NP) ||
					app.user?.claims?.mark && app.user?.prof?.xtra?.show_wr_stats;				
				break;

			case "pts":
				r = app.user.user_in && this.deny_all(["admin", "nurse", "mang", "doc", "pharm"]);
				break;

			case "bookable_staff":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.user.claims?.doc ||
					app.user?.claims?.nurse;
				break;

			case "discounts":
				r =
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					app.user?.claims?.mark && app.user?.prof?.xtra?.can_discount ||
					org_model.org_conf_itm(app.user.org, "can_discount") ||
					org_model.org_conf_itm(app.user.org, "give_discount");
				break;

			case "kb_admin":
				r = app.user?.claims?.admin; //|| org_model.org_conf_itm(app.user.org, 'can_discount')
				break;

			case "kb_view":
				r = app.user;
				break;

			case "mims:mws:test-check-interactions":
				r = Boolean(app.user?.claims?.admin);
				break;
			case "mims:prescribing-rules:list":
				r = Boolean(app.user?.claims?.admin || app.user?.claims?.supp);
				break;
			case "mims:prescribing-rules:manage":
				r = Boolean(app.user?.claims?.admin);
				break;
			case "admin:pricing-shadow:list":
				r = Boolean(app.user?.claims?.admin);
				break;
			case "show_medcat":
			case "avail_medcat":
				// const cat_flat_list = (await cat_model.get_cat_list())?.cat_flat_list;
				cat_nm = p?.cat_nm || "pharm";
				const medcat_list =
					app.app_data.ws_data?.cat_data?.[cat_nm]?.[
						(a == "show_medcat" && "cat_show_list") || (a == "avail_medcat" && "cat_avail_list")
					] || [];
				r =
					// app.user?.claims?.root

					// || !app.dvc.org?.conf?.excl_med_cat && !app.user.org?.conf?.excl_med_cat

					// ||
					(p.k == "prescription" ||
						(!app.dvc.org?.conf?.excl_med_cat?.includes(p.k) &&
							!app.user.org?.conf?.excl_med_cat?.includes(p.k) &&
							!app.runtime.org?.conf?.excl_med_cat?.includes(p.k) &&
							// && (p.cat_nm && p.cat_nm!='pharm' || p.alt_cat && p.alt_cat=='_cosm' || !!cat_flat_list[p.k])
							!!(p.ignore_cache || medcat_list[p.k])) ||
						// && Object.keys(cat_flat_list).filter( k => k == p.k ).length>0
						// && (await cat_model.get_cat_list()).cat_flat_list.filter( k => k == p.k ).length>0
						// && app.app_data.ws_data.cat_data[cat_model.FRDB_LOC].cat_flat_list.filter( k => k == p.k ).length>0

						app.user?.prof?.pts_prescs?.filter?.(pr => pr?.med?.mid == p.k)?.length ||
						consults_for_followup(p.k).length > 0 ||
						consults_for_weight_loss_followup(p.k).length > 0 ||
						app.dvc?.org?.conf?.incl_med_cat?.includes(p.k) ||
						app.user?.org?.conf?.incl_med_cat?.includes(p.k) ||
						app.runtime?.org?.conf?.incl_med_cat?.includes(p.k) ||
						app.runtime?.incl_med_cat?.includes(p.k)) &&
					// || app.dev_env

					// || (document.location.hostname=='localhost' && document.location.port=='9011')
					// || !app.dvc.org && p!="test-cat"
					(!app.dvc.org.conf?.no_medcerts || p != "-LWR6ThuXqNL3jl3hEVv");

				DEBUG && console.log("check_access ", a, p, r);
				break;

			case "show_medcat_app":
				cat_nm = p?.cat_nm || "pharm";
				r = !!app.app_data.ws_data?.cat_data?.[cat_nm]?.cat_show_list?.[p.k];
				break;

			case "consult_stats":
				r = app.user?.claims?.admin || app.user?.claims?.doc;
				break;

			case "doc_noti":
				r =
					app.user?.claims?.root ||
					app.user?.claims?.admin ||
					app.user?.claims?.supp ||
					(app.user?.claims?.doc && org_model.org_conf_itm(app.user.org, "is_GP"));
				break;

			case "script_appr":
				r =
					app.user?.claims?.root ||
					app.user?.claims?.admin ||
					(app.user?.claims?.doc && org_model.org_conf_itm(app.user.org, "is_GP"));
				break;

			case "onboard_doc":
				r = app.acl.is_admin || app.acl.is_supp;
				break;

			case "qrgen":
				r = app.acl.is_admin || app.acl.is_supp || app.user?.claims?.mang || app.user?.claims?.mark;
				break;

			case "statsop":
				r = app.acl.is_admin;
				break;

			case "appr_algo":
				r = app.acl.is_admin;
				break;

			case "supp_patho":
				r = app.acl.is_admin || app.acl.is_supp_patho;
				break;

			case "followup_letter":
				r =
					app.user?.claims?.root ||
					app.acl.is_admin ||
					app.acl.is_supp ||
					app.user?.prof?.xtra?.show_followup_letter;
				break;

			case "🍞":
				r = app.acl.is_admin || app.acl.is_supp || app.acl.is_doc;
				break;

			case "view_static_wr":
				r = false;
				break;

			case "view_full_wr":
				r = app.acl.is_admin || app.acl.is_supp;
				break;

			case "inventory":
				r = app.acl.is_mang || app.user?.claims?.admin; //&& ['optional', 'mandatory'].includes(org_model.org_conf_itm(app.user.org, 'inventory_access'))
				break;
			case "drug-book:view":
				r = app.acl.is_mang || app.acl.is_nurse || app.user?.claims?.admin;
				break;
			case "inventory:view":
				r = app.acl.is_mang || app.user?.claims?.admin;
				break;
			case "inventory:can-use":
				r = app.acl.is_mang || app.user?.claims?.admin;
				break;
			case "inventory:add":
				r = app.acl.is_mang || app.acl.is_admin; //&& ['optional', 'mandatory'].includes(org_model.org_conf_itm(app.user.org, 'inventory_access'))
				break;
			case "inventory:fulldata":
				r = app.user?.claims?.admin;
				break;
			case "inventory_admin":
				r = app.user?.claims?.admin || app.user?.claims?.supp || app.user?.claims?.mang;
				break;
			case "can_take_cosm_consults":
				r = app.acl.is_cosm_doc || app.acl.is_admin || app.acl.is_supp;
				break;
			case "idp-user:audit:view":
				r = app.acl.is_admin || app.acl.is_supp;
				break;
		}

		return !!r;
	}

	static check_any(a) {
		if (!Array.isArray(a)) {
			a = [a];
		}

		for (const n of a) {
			if (this.check_access(n)) {
				return true;
			}
		}

		return false;
	}

	static check_all(a) {
		if (!Array.isArray(a)) {
			a = [a];
		}

		for (const n of a) {
			if (!this.check_access(n)) {
				return false;
			}
		}

		return true;
	}

	static deny_all(a) {
		return !this.check_any(a);
	}

	// ----	--------------------------------------------	--------------------------------------------
	
	static limit_access(a, p, nice, goto_after_login) {
		/*
			We have certian follow up consults - which are linked in emails.
			If the user clicks on the link, we want to redirect them back to the consult after login
		*/
		const REDIRECT_TO_CONSULTS = [
			'/m/pharm/DocCons/MenoConsFU'
		]
		if (app.history.location.pathname == "/profile") {
			goto_after_login = "/profile";
		}
		else if (REDIRECT_TO_CONSULTS.includes(app.history.location.pathname)) {
			goto_after_login = app.history.location.pathname;
		}
		const r = this.check_access(a, p);

		
		if (!r) {
			setTimeout(_ => {
				if (!nice) {
					app.trigger(app.events.SHOW_MESSAGE, "No Access", "Please Login", "negative", "user_auth_msg");
				} else {
					(app.user.user_in &&
						app.trigger(
							app.events.SHOW_MESSAGE,
							"No Access",
							"You do not have access to this functionality with this account / on this device.",
							"yellow",
							"user_auth_msg",
						)) ||
						app.trigger(
							app.events.SHOW_MESSAGE,
							"A user account is required to proceed.",
							"Please login in to your existing account or create a new account.",
							"positive",
							"user_auth_msg",
						);
				}

				if (app.dev_env) console.log(`NO ACCESS:  `, a, p);

				app.goto_after_login = goto_after_login;

				if (app.settings.icosm) {
					app.history.push((app.settings.redirect_to_signup && "/login/signup") || "/");
					// !app.settings.redirect_to_signup && app.trigger(app.events.REQUEST_LOGIN)
				} else {
					app.history.push(app.settings.redirect_to_signup ? "/login/signup" : "/login");
				}
			}, 0);
		}

		return r;
	}

	// ----	--------------------------------------------	--------------------------------------------
	// ----	--------------------------------------------	--------------------------------------------

	static async load_script_pts_bysid(sid, enc) {
		return await API_service.load_data(
			"User/scrPts",
			{ sid, enc },
			// r => ca && ca(r)
		);
	}

	// ----	--------------------------------------------	--------------------------------------------
	// ----	--------------------------------------------	--------------------------------------------

	static async full_logout(force = false) {
		if (
			force ||
			(await app.confirm(`Full logout?`, `This will completely log you out of all your devices.`)) == "yes"
		) {
			await API_service.load_data("User/fullLogout", {});
			gtm.log("", "full_logout");
			user_model.logout();
		}
	}

	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------

	static async onUserLoggedIn(user/** @type {import("@firebase/auth").User}*/) {
		logger.log_api('user_login', { type: user.providerId }, undefined, undefined, {
			// This fires before the rest of our app updates and registers the user ID token etc. to be available
			// in API_Service. So pass the token here since we know it and want this to be an authed request
			token: user.accessToken,
		});
	}

	static async logout() {
		logger.log_api('user_logout', undefined, undefined, undefined, {
			// Similar to above, but without this token, API_Service will await idtoken(). So unless we also await
			// here, the super.logout() below may cause the token to be lost before the event fires. We know the
			// user token here (hopefully!), so ensure we use it
			token: app.user?.token,
		});

		window.Intercom("shutdown");
		if (!app.settings.is_booth) {
			intercom.boot({
				app_id: "ksq5hplh",
				hide_default_launcher: !app.site_status?.use_intercom || app.settings.is_cbls || app.settings.is_cmg,
			});
		}
		if (app.settings.icosm) {
			// Clear the cookie if there is one
			await API_service.api_promise("sessionLogout", {}, { credentials: true, no_hdr: true });
		}
		super.logout();
	}

	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------

	static async usr_signup(p) {
        const ret = await API_service.load_data(
			"User/regUsr",
			{ ...p, runt: app.runtime },
			// r => ca && ca(r)
		);
		const success = ret?.res == "ok";
		gtm.auth(success ? "signup" : "signup_fail", { type: "usr_signup" });
		return ret;
	}

	// 		--------------------------------		--------------------------------		---------

	static async pts_signup(s, p) {
		const ret = await API_service.load_data(
			"User/regPts",
			{ sid: s, ...p },
			// r => ca && ca(r)
		);

		const success = ret?.res == "ok";
		gtm.auth(success ? "signup" : "signup_fail", { type: "pts_signup", sid: s });
		return ret;
	}

	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------

	static async load_prof(uid, p) {
		const res = await API_service.load_data(
			"User/loadUserProf",
			{ uid, ...p, runt: app.runtime, },
			// r => ca && ca(r)
		);
		app.runtime.uip = res.uip || null;
		// Moving gtm tagging to app_user so as to not consumer new_prof in multiple places
		return res;
	}

	static async updatePassword(encryptedPassword) {
		return API_service.api_promise("User/updatePassword", { encryptedPassword }).then(rejectOnResErr);
	}

	static async save_prof(d, ca, target) {
		if (!target) {
			target = (d.uid && app.user.profs.find(p => p.uid == d.uid)) || (!d.uid && d.parent && {}) || app.user.prof;
		}

		if (!target) return ca?.({ res: "error" });

		if (d?.dva?.colour == "" && d?.dva?.number == "") {
			d.dva = null;
		}

		if (d?.height || d?.weight) {
			const extras = {
				...target?.extras,
				...(d?.height && { height: d.height }),
				...(d?.weight && { weight: d.weight }),
			};
			delete d?.height;
			delete d?.weight;
			d.extras = extras;
		}

		const prof_diff = obj_diff(target, d);

		DEBUG && console.log("user_model :: save_prof", app.user.uid, d.uid, prof_diff, Object.keys(prof_diff).length);

		if (!prof_diff.to || !Object.keys(prof_diff.to).length) return ca?.({ res: "ok" });

		let params = { ...d, prof_diff };

		if (prof_diff.to.first_name || prof_diff.to.last_name)
			params.newname =
				(prof_diff.to.first_name || target.first_name) + " " + (prof_diff.to.last_name || target.last_name);

		try {
			return await API_service.load_data("User/updateUserProfile", params, r => {
				if (r.res === "ok") {
					if (r.profile?.uid === app.user.uid) {
						app.user.set_profile(r.profile);
						app.trigger(app.events.USER_UPD, "ProfileUpdated");
					}
				}
				ca && ca(r);
			});
		} catch (e) {
			ca?.({ res: "error", err_msg: e.message || e });
		}
	}

	static async unlink_prof({ uid, pid }, ca) {
		DEBUG && console.log("user_model :: unlink_prof", uid, pid);

		return await API_service.load_data("User/unlinkProfile", { uid, pid }, r => ca && ca(r));
	}

	static async save_pref(pref, value, save = true) {
		// TODO probably not the best way to do this.

		const user = app.user;

		DEBUG && console.log("user_model :: save_pref", user.uid);

		const prefs = { ...user.prefs, [pref]: value };

		app.user.prefs = prefs;

		app.trigger(app.events.USER_UPD, "PrefsUpdated");

		const extras = { ...app.user.prof.extras };

		extras.prefs = prefs;

		return save && user_model.save_prof({ uid: user.uid, extras });
	}

	static refresh_prof() {
		setTimeout(async _ => {
			const prof_data = await API_service.load_data("User/loadUserProf", { uid: app.user.uid });
			if (prof_data) {
				app.user.set_profile(prof_data);
				app.trigger(app.events.USER_UPD, "ProfileUpdated");
			}
			user_model.refresh_prof();
		}, 5000);
	}

	// 		--------------------------------		--------------------------------		---------

	static async load_ihi(uid, parent) {
		return API_service.api_promise("User/myIhi", { uid, parent }).then(rejectOnResErr);
	}

	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------

	static save_card(CC_tok, name, ccnum, exp, ca) {
		DEBUG && console.log("save_card()", { CC_tok, name, ccnum, exp });
		const short = ccnum.substring(ccnum.length - 4);
		const add_tm = moment().tz("Australia/Melbourne").format("D/M/YY HH:mm:ss");
		const card = {
			CC_tok,
			short,
			name,
			exp,
			add_tm,
		};

		const ccs = app.user.prof.ccs || [];
		let i = ccs.findIndex(el => {
			return el.short == short && el.exp == exp;
		});
		if (i != -1) {
			ccs[i] = card;
		} else {
			ccs.unshift(card);
			i = 0;
		}
		DEBUG && console.log({ ccs });
		user_model.save_prof({ ccs: [...ccs] }, result => {
			ca && ca(card, i);
		});
	}

	static delete_card(card, ca) {
		const ccs = (app.user.prof.ccs && [...app.user.prof.ccs]) || [];
		let i = ccs.findIndex(el => {
			return el == card;
		});
		DEBUG && console.log({ ccs, i });
		if (i != -1) {
			ccs.splice(i, 1);
			user_model.save_prof({ ccs }, result => {
				ca && ca();
			});
		}
	}

	static primary_card(card, ca) {
		const ccs = (app.user.prof.ccs && [...app.user.prof.ccs]) || [];
		const filtered = ccs.filter(el => el != card);
		DEBUG && console.log({ ccs });
		user_model.save_prof({ ccs: [card, ...filtered] }, result => {
			ca && ca();
		});
	}

	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------
	// 		--------------------------------		--------------------------------		---------

	static role_name(c, top_only = false) {
		if (!c) return null;

		let n = [];

		Object.values(_ROLE_LIST).map(r => c[r.c] && n.push(r.n));

		return (top_only && n.reverse()[0]) || n.reverse().join(", ");
	}

	// ----	--------------------------------------------	--------------------------------------------
	// ----	--------------------------------------------	--------------------------------------------

	static async list_idp_users(offset = 0, limit = 50, filters = {}) {
		const { tenantId, ...otherFilters } = obj_filter_by_key(filters, (_, value) => value !== null);
		return API_service.api_promise("idp-user/list", { offset, limit, tenantId, filters: otherFilters })
			.then(rejectWithResErr)
			.then(res => res.data)
			.catch(ensureIsError);
	}

	static async idp_user_action(action, params) {
		return API_service.api_promise(`idp-user/${action}`, params)
			.then(rejectWithResErr)
			.then(res => res.data)
			.catch(ensureIsError);
	}

	static async user_admin_action(action, params) {
		return API_service.api_promise(`UserAdmin/${action}`, params)
			.then(rejectWithResErr)
			.then(res => res.data)
			.catch(ensureIsError);
	}

	// ----	--------------------------------------------	--------------------------------------------
	// ----	--------------------------------------------	--------------------------------------------

	static async set_user_newemail(u, newemail, ca) {
		return await API_service.load_data(
			"User/setUserNewEmail",
			{ uid: u.prof?.par || u.uid, newemail },
			r => ca && ca(r),
		);
	}

	static is_new_profile(profile) {
		DEBUG && console.log("is_new_profile", profile);
		if (!profile) return false;

		const fieldsNotInitiallyFilledOut = [
			field.mobile,
			field.full_address,
			field.chro_cond,
			field.cons_medhist,
			field.cons_allergy,
			field.cons_has_allergies,
		].map(field => field.name);

		const completedFields = fieldsNotInitiallyFilledOut.filter(
			field => field in profile && profile[field] !== null,
		);
		DEBUG && console.log("is_new_profile", completedFields);
		return completedFields.length === 0;
	}

	static hasCoreAccountDetails(profile) {
		return Boolean(
			profile?.sex &&
			profile?.dob &&
			profile?.mobile &&
			profile?.full_address && field.full_address.validate_function(profile.full_address)
		);
	}
}
